import $axios from "../../api";

// Bentuk paginator kosong. Dipakai sebagai nilai awal DAN sebagai cadangan di
// SET_LIST: kalau server tidak mengirim apa pun, `list` harus tetap objek dengan
// `total`/`per_page` bertipe angka. Membiarkannya `undefined` membuat
// <b-pagination> kehilangan :total-rows dan kontrol halamannya lenyap tanpa error.
const emptyList = () => ({
  data: [],
  total: 0,
  per_page: 20,
  from: 0,
  to: 0,
});

// Bentuk kosong pratinjau sync — alasan yang sama dengan emptyList(): halaman
// membaca `total_missing` dan `rows` tanpa penjaga, jadi keduanya harus selalu
// bertipe benar meski server tak mengirim apa-apa.
const emptySyncPreview = () => ({
  total_missing: 0,
  batch_size: 20,
  remaining_after_batch: 0,
  defaults: {},
  source: {},
  rows: [],
});

const state = () => ({
  isLoading: false,
  isErrors: "",
  isSuccess: false,
  page: 1,
  list: emptyList(),
  options: {
    areas: [],
    types: [],
    kanvas: [],
  },

  // ---- sync dari mirror SAP `owhs` ----
  syncLoading: false,
  // Flag simpan TERPISAH dari syncLoading: yang pertama mengisi tabel, yang
  // kedua menonaktifkan tombol Simpan. Menyatukannya membuat seluruh tabel
  // tampak "memuat" saat yang berjalan cuma tombolnya.
  syncSaving: false,
  syncPreview: emptySyncPreview(),
  // Kunci bertitik MENTAH dari Laravel (`rows.3.warehouse_code`). Jangan
  // dibentuk ulang di sini — halaman butuh bentuk aslinya untuk menandai
  // input per baris, getter di bawah yang meratakannya untuk daftar pesan.
  syncErrors: {},
  // Baris yang benar-benar dikirim, disimpan SEBELUM request. Tanpa ini,
  // indeks pada kunci error tidak bisa ditautkan kembali ke kode gudangnya.
  syncPayload: [],
  syncResult: null,
});

const mutations = {
  SET_LOADING(state, payload) {
    state.isLoading = payload;
  },
  ASSIGN_ERRORS(state, payload) {
    state.isErrors = payload;
  },
  SET_SUCCESS(state, payload) {
    state.isSuccess = payload;
  },
  SET_PAGE(state, payload) {
    state.page = payload;
  },
  SET_LIST(state, payload) {
    state.list = payload || emptyList();
  },
  SET_OPTIONS(state, payload) {
    state.options = {
      areas: (payload && payload.areas) || [],
      types: (payload && payload.types) || [],
      kanvas: (payload && payload.kanvas) || [],
    };
  },
  SET_SYNC_LOADING(state, payload) {
    state.syncLoading = payload;
  },
  SET_SYNC_SAVING(state, payload) {
    state.syncSaving = payload;
  },
  SET_SYNC_PREVIEW(state, payload) {
    state.syncPreview = {
      ...emptySyncPreview(),
      ...(payload || {}),
      rows: (payload && payload.rows) || [],
    };
  },
  SET_SYNC_ERRORS(state, payload) {
    state.syncErrors = payload || {};
  },
  SET_SYNC_PAYLOAD(state, payload) {
    state.syncPayload = payload || [];
  },
  SET_SYNC_RESULT(state, payload) {
    state.syncResult = payload || null;
  },
};

const getters = {
  /**
   * Ratakan error bertitik Laravel jadi daftar yang bisa dibaca manusia.
   *
   * Pembagian tugasnya sengaja sama dengan stores/inv/StockOpname.js: peta
   * bertitik tetap utuh di state supaya halaman bisa menandai input per baris
   * (`syncErrors['rows.3.name']`), sementara getter ini hanya menambahkan
   * IDENTITAS barisnya. Pesan "apa yang salah" sudah datang dari server dalam
   * Bahasa Indonesia, jadi di sini tidak ada prosa yang perlu dirakit.
   */
  syncErrorList: (state) =>
    Object.keys(state.syncErrors || {}).map((key) => {
      const part = key.split("."); // rows.3.warehouse_code
      const index = Number(part[1]);
      const row = (state.syncPayload || [])[index];
      const code = row && row.warehouse_code;

      return {
        key,
        index,
        field: part[2],
        message:
          state.syncErrors[key][0] +
          (code ? ` (Kode: ${code}, baris ke-${index + 1})` : ""),
      };
    }),
};

const actions = {
  getList({ commit, state, rootState, dispatch }, payload = "") {
    commit("SET_LOADING", true);

    return new Promise((resolve, reject) => {
      $axios
        .get(
          `inv/warehouse-union/list?page=${state.page}&view=${rootState.Main.view}${payload}`
        )
        .then((response) => {
          commit("SET_LIST", response.data.data);
          commit("SET_LOADING", false);
          resolve(response);
        })
        .catch((error) => {
          commit("SET_LOADING", false);
          // ⚠️ Sengaja TIDAK dibisukan. Di stores/inv/Warehouse.js baris ini
          // dikomentari, dan akibatnya 403 dari checkAccess tampil sebagai tabel
          // kosong tanpa satu pun petunjuk — pengguna melapor "datanya hilang",
          // padahal aksesnya yang belum diberikan.
          dispatch("Main/globalSweetAlert", error.response, { root: true });
          reject(error);
        });
    });
  },

  getOptions({ commit, dispatch }) {
    return $axios
      .get(`inv/warehouse-union/options`)
      .then((res) => {
        commit("SET_OPTIONS", res.data.data);
        return res;
      })
      .catch((error) => {
        dispatch("Main/globalSweetAlert", error.response, { root: true });
        throw error;
      });
  },

  createWarehouse({ commit }, payload) {
    commit("SET_SUCCESS", false);
    commit("SET_LOADING", true);
    commit("ASSIGN_ERRORS", null);

    return new Promise((resolve, reject) => {
      $axios
        .post(`inv/warehouse-union/create`, payload)
        .then((response) => {
          commit("SET_LOADING", false);
          commit("SET_SUCCESS", true);
          resolve(response);
        })
        .catch((error) => {
          commit("ASSIGN_ERRORS", error.response && error.response.data.errors);
          commit("SET_LOADING", false);
          commit("SET_SUCCESS", false);
          reject(error);
        });
    });
  },

  updateWarehouse({ commit }, payload) {
    commit("SET_SUCCESS", false);
    commit("SET_LOADING", true);
    commit("ASSIGN_ERRORS", null);

    return new Promise((resolve, reject) => {
      $axios
        .put(`inv/warehouse-union/update`, payload)
        .then((response) => {
          commit("SET_LOADING", false);
          commit("SET_SUCCESS", true);
          resolve(response);
        })
        .catch((error) => {
          commit("ASSIGN_ERRORS", error.response && error.response.data.errors);
          commit("SET_LOADING", false);
          commit("SET_SUCCESS", false);
          reject(error);
        });
    });
  },

  // ---- sync dari mirror SAP `owhs` ----

  getSyncPreview({ commit, dispatch }, { exclude = [] } = {}) {
    commit("SET_SYNC_LOADING", true);

    const params = new URLSearchParams();

    // exclude[]=A&exclude[]=B — bentuk array yang dibaca Laravel apa adanya.
    exclude.forEach((code) => params.append("exclude[]", code));

    const query = params.toString();

    return new Promise((resolve, reject) => {
      $axios
        .get(`inv/warehouse-union/sync/preview${query ? `?${query}` : ""}`)
        .then((response) => {
          commit("SET_SYNC_PREVIEW", response.data.data);
          commit("SET_SYNC_LOADING", false);
          resolve(response);
        })
        .catch((error) => {
          commit("SET_SYNC_LOADING", false);
          dispatch("Main/globalSweetAlert", error.response, { root: true });
          reject(error);
        });
    });
  },

  saveSyncRows({ commit, dispatch }, rows) {
    // Disimpan SEBELUM request: kunci error dari server berupa indeks
    // (`rows.3.name`), dan tanpa payload-nya indeks itu tidak bisa
    // diterjemahkan kembali jadi kode gudang di pesan.
    commit("SET_SYNC_PAYLOAD", rows);
    commit("SET_SYNC_ERRORS", {});
    commit("SET_SYNC_RESULT", null);
    commit("SET_SYNC_SAVING", true);

    return new Promise((resolve, reject) => {
      $axios
        .post(`inv/warehouse-union/sync/store`, { rows })
        .then((response) => {
          commit("SET_SYNC_SAVING", false);
          commit("SET_SYNC_RESULT", response.data.data);
          // ⚠️ SENGAJA tidak commit SET_SUCCESS. Flag itu milik modal
          // create/edit — watcher-nya menutup modalWarehouse dan memanggil
          // handleSearch(), jadi memakainya di sini memicu penutupan modal
          // hantu plus pengambilan daftar ganda.
          dispatch("Main/globalSuccessSweetAlert", response.data.message, {
            root: true,
          });
          resolve(response);
        })
        .catch((error) => {
          commit("SET_SYNC_SAVING", false);

          const status = error.response && error.response.status;

          if (status === 422) {
            // Kunci bertitik disimpan mentah — halaman yang menandai barisnya.
            commit("SET_SYNC_ERRORS", error.response.data.errors || {});
          } else {
            dispatch("Main/globalSweetAlert", error.response, { root: true });
          }

          reject(error);
        });
    });
  },

  showhide({ commit, dispatch }, payload) {
    commit("SET_SUCCESS", false);

    return new Promise((resolve, reject) => {
      $axios
        .put(`inv/warehouse-union/showhide`, payload)
        .then((response) => {
          commit("SET_SUCCESS", true);
          dispatch("Main/globalSuccessSweetAlert", response.data.message, {
            root: true,
          });
          resolve(response);
        })
        .catch((error) => {
          commit("SET_SUCCESS", false);
          dispatch("Main/globalSweetAlert", error.response, { root: true });
          reject(error);
        });
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
